/**
 * Every rule the web member detail pane states, as pure functions (ISSUE-194).
 *
 * This module imports no React and no generated SDK, so its rules can be
 * tested one at a time without a render. The pane draws what this file decides
 * and decides nothing itself; that the pane actually draws it is proved
 * separately, by `PoolMemberDetailPane.test.tsx`.
 *
 * FOUR THINGS HERE ARE PRODUCT DECISIONS, NOT PLUMBING:
 *
 *  1. **A grant carries no archived state and this file never invents one.**
 *     `/award/get-for-member` returns a name, a color, a kind and a time, and
 *     nothing else. A chip for an archived type is therefore the same object
 *     as a chip for an active one, which is what SUR-R92 and Q262 ask for:
 *     telling somebody their medal is archived reads as a demotion of
 *     something they earned. There is no branch to remove later because there
 *     is no branch.
 *
 *  2. **The season switch moves the counts, never the chips.** A grant carries
 *     `givenAt` and no season flag, so every chip is built with
 *     `isThisSeason: true` and `chipsForScope` keeps the full list in both
 *     modes. Working out "this season" from a timestamp on the client is a
 *     client re-deriving a contest rule, which
 *     `.agents/rules/server-resolves-client-renders.mdc` forbids outright. R2
 *     asks for the switch over the counts and, separately, for every custom
 *     award shown in full, so the full list stands in both modes.
 *
 *  3. **`canManage` comes from the server and your own screen never manages
 *     you.** The client never works out who may manage from a role. It only
 *     adds that nobody manages themselves from here.
 *
 *  4. **A plain member gets no role label at all**, not the word "Member"
 *     (GLOSSARY-1). That rule lives in `roleLabel`, which is imported rather
 *     than restated.
 */
import { type typeAwardChip } from "../awards/awardChips";
import { type typeAwardCounts, ZERO_AWARD_COUNTS } from "../awards/awardCounts";
import { roleLabel } from "../poolMembers/memberRowFacts";
import { poolMarkInitials } from "../pools/poolsListState";

export type typeMemberDetailPhase = "noIds" | "loading" | "missing" | "ready";

export type typeMemberContestLink = {
  contestId: string;
  contestName: string;
};

export type typeMemberDetailModel = {
  phase: typeMemberDetailPhase;
  displayName: string;
  usernameLabel: string | null;
  initials: string;
  avatarUrl: string | null;
  roleLabel: string | null;
  isPlayingNow: boolean;
  isSelf: boolean;
  showMessage: boolean;
  showManageControls: boolean;
  areAwardsLoading: boolean;
  haveAwardsFailed: boolean;
  thisSeason: typeAwardCounts;
  allTime: typeAwardCounts;
  chips: readonly typeAwardChip[];
  contests: readonly typeMemberContestLink[];
};

/*
 * `contestStatus` is deliberately NOT on this model. The mobile twin carries it
 * because its manage control builds a href out of it. Web's manage control
 * navigates to the roster's own actions dialog, which re-derives the status
 * from `/member/list`, so carrying a second copy here would be a field nothing
 * reads and a second source for one fact.
 */

export type typeMemberDetailMember = {
  displayName: string;
  username: string;
  avatarUrl?: string | null | undefined;
  poolRole: "owner" | "admin" | "member";
  isPlayingNow?: boolean | null | undefined;
  activeContests: { contestId: string; contestName: string }[];
  canManage: boolean;
};

export type typeMemberDetailAwards = {
  thisSeason: typeAwardCounts;
  allTime: typeAwardCounts;
  customAwardHistory: {
    awardGrantId: string;
    awardTypeId: string;
    name: string;
    color: string;
    kind: "medal" | "trophy";
    givenAt: string;
  }[];
};

const EMPTY_MODEL: typeMemberDetailModel = {
  phase: "loading",
  displayName: "",
  usernameLabel: null,
  initials: "",
  avatarUrl: null,
  roleLabel: null,
  isPlayingNow: false,
  isSelf: false,
  showMessage: false,
  showManageControls: false,
  areAwardsLoading: true,
  haveAwardsFailed: false,
  thisSeason: ZERO_AWARD_COUNTS,
  allTime: ZERO_AWARD_COUNTS,
  chips: [],
  contests: [],
};

export function buildMemberDetailModel(input: {
  poolId: string;
  isLoading: boolean;
  areAwardsLoading: boolean;
  member: typeMemberDetailMember | null;
  awards: typeMemberDetailAwards | null;
  haveAwardsFailed: boolean;
  viewerUserId: string;
  targetUserId: string;
}): typeMemberDetailModel {
  // An unknown viewer is never "self", so a slow profile read cannot hide the
  // Message action from somebody entitled to it.
  const isSelf =
    input.viewerUserId !== "" && input.viewerUserId === input.targetUserId;

  if (input.poolId === "" || input.targetUserId === "") {
    return { ...EMPTY_MODEL, phase: "noIds" };
  }

  if (input.isLoading) {
    return { ...EMPTY_MODEL, isSelf };
  }

  if (input.member === null) {
    return { ...EMPTY_MODEL, phase: "missing", isSelf };
  }

  const member = input.member;
  const avatarUrl =
    member.avatarUrl === undefined || member.avatarUrl === ""
      ? null
      : member.avatarUrl;

  return {
    phase: "ready",
    displayName: member.displayName,
    usernameLabel: member.username === "" ? null : `@${member.username}`,
    initials: poolMarkInitials(member.displayName),
    avatarUrl,
    roleLabel: roleLabel(member.poolRole),
    // Only a `true` from the server is a yes. A null means the server did not
    // say, and a playing-now dot beside somebody who is not playing is a lie.
    isPlayingNow: member.isPlayingNow === true,
    isSelf,
    // D5. You cannot message yourself, so the action is not offered.
    showMessage: !isSelf,
    // D4. The server decided who may manage; this only adds that nobody
    // manages themselves from here.
    showManageControls: member.canManage && !isSelf,
    // The awards section loads and fails on its own. A name and a contest list
    // are still worth reading when the award service is having a bad minute.
    areAwardsLoading: input.areAwardsLoading,
    haveAwardsFailed: input.haveAwardsFailed,
    thisSeason: input.awards?.thisSeason ?? ZERO_AWARD_COUNTS,
    allTime: input.awards?.allTime ?? ZERO_AWARD_COUNTS,
    chips: (input.awards?.customAwardHistory ?? []).map((grant) => ({
      awardGrantId: grant.awardGrantId,
      poolId: input.poolId,
      // The grant carries no contest, and every entry in customAwardHistory is
      // a custom grant by construction.
      contestName: null,
      kind: grant.kind,
      source: "custom" as const,
      // D9. The name and the color are read off the type on every response, so
      // a rename or a recolor reaches every holder with no work here.
      name: grant.name,
      color: grant.color,
      // Deliberate, and NOT a placeholder. See point 2 in this file's header:
      // the switch moves the counts and never the chips, so every chip stays
      // in both modes. Do not "fix" this into a `givenAt` comparison; that
      // would be the client re-deriving a season boundary.
      isThisSeason: true,
    })),
    contests: member.activeContests.map((contest) => ({
      contestId: contest.contestId,
      contestName: contest.contestName,
    })),
  };
}
